import { getValidLocale } from '@unifyapps/i18n/utils/getValidLocale';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { DayPickerLocale } from 'react-day-picker';
import { getLocaleConfig, LOCALE_CONFIGS } from './dayPickerLocaleConfigs';

export function getWeekStartsOn(language: string | undefined): 0 | 1 | 6 {
  return getLocaleConfig(language)?.weekStartsOn ?? 0;
}

const loadedLocales = new Map<string, DayPickerLocale>();
const listeners = new Set<() => void>();

function subscribe(onLocaleLoaded: () => void) {
  listeners.add(onLocaleLoaded);
  return () => {
    listeners.delete(onLocaleLoaded);
  };
}

const getServerSnapshot = () => undefined;

// keyed on the normalized locale, not the raw string, so 'fr' and 'fr-FR' share one cache entry
export function preloadDayPickerLocale(language: string | undefined) {
  const locale = getValidLocale(language);
  if (!locale || loadedLocales.has(locale)) return;

  const config = LOCALE_CONFIGS.get(locale);
  if (!config) return;

  void config.load().then(
    (resolved) => {
      loadedLocales.set(locale, resolved);
      for (const notify of listeners) notify();
    },
    (error: unknown) => {
      console.error(`Failed to load DatePicker locale "${locale}"`, error);
    },
  );
}

export function useDayPickerLocale(language: string | undefined): DayPickerLocale | undefined {
  const locale = getValidLocale(language);

  const getSnapshot = useCallback(() => (locale ? loadedLocales.get(locale) : undefined), [locale]);

  useEffect(() => {
    preloadDayPickerLocale(language);
  }, [language]);

  // not useState — that flashes English on a picker opened after the preload already finished
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
